/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Anchor, List, Loader } from '@mantine/core';
import { AlertCircle } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import AppCard from '../../../../common/AppCard';
import AppFlexbox from '../../../../common/AppFlexbox';
import { currencyFormat } from '../../../../../helpers/format';
import { isCartVariantUnavailable } from '../../../../../helpers/webstoreHelper';

const WsUnavailableProductsAlert = ({
  ecomStoreCart,
  lineItemsInfo,
  mediaQueries,
  baseStoreUrl,
  onNavigate,
  submitting,
  onRemoveUnavailableProducts,
  isDrawerView
}) => {
  const { isTabletOrSmaller } = mediaQueries;

  const { ecomStoreCartProducts, latestVersionEcomStoreCartProducts } =
    ecomStoreCart || {};

  const latestVariants = latestVersionEcomStoreCartProducts || [];
  const variantMessageData = latestVariants
    .map((latestVariant) => {
      const cartProduct = ecomStoreCartProducts.find(
        (c) =>
          c.ecomStoreProductVariant.ecomVendorProductVariant.uuid ===
          latestVariant.ecomVendorProductVariant.uuid
      );
      const isUnavailable = isCartVariantUnavailable(latestVariant);

      return {
        key: latestVariant.ecomVendorProductVariant.uuid,
        fkEcomStoreCartPackage: cartProduct.fkEcomStoreCartPackage,
        productName:
          cartProduct.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
            .name,
        isUnavailable,
        oldPrice: cartProduct.ecomStoreProductVariant.price,
        newPrice: latestVariant.price,
        urlHandle: !isUnavailable
          ? latestVariant.ecomStoreProduct.urlHandle
          : null
      };
    })
    .filter((f) => !f.fkEcomStoreCartPackage);

  const packageMessageData = lineItemsInfo
    .map((info) => ({
      key: info.entityId,
      uuid: info.uuid,
      ecomStoreCartPackage: info.ecomStoreCartPackage,
      productName:
        info.ecomStoreCartPackage?.ecomStoreProductPackage.ecomVendorBundle
          .name ||
        info.ecomStoreCartProduct?.ecomStoreProductVariant.ecomStoreProduct
          .ecomVendorProduct.name,
      isBundleProduct: info.isBundleProduct,
      requiresProductUpdate: info.requiresProductUpdate,
      requiresPrintImagesUpdate: info.requiresPrintImagesUpdate,
      hasChanges: info.hasChanges,
      isUnavailable: info.isUnavailable,
      urlHandle: !info.isUnavailable ? info.urlHandle : null
    }))
    .filter(
      (m) =>
        (m.isUnavailable ||
          m.hasChanges ||
          m.requiresProductUpdate ||
          m.requiresPrintImagesUpdate) &&
        (!!m.ecomStoreCartPackage ||
          !latestVariants.some(
            (p) => p.ecomVendorProductVariant.uuid === m.uuid
          ))
    );

  const cartMessageData = [...packageMessageData, ...variantMessageData];

  return (
    cartMessageData.length > 0 && (
      <AppCard
        radius={isTabletOrSmaller ? 0 : 'md'}
        shadow="lg"
        style={{ flex: 1, padding: 0, width: '100%' }}
        withBorder
      >
        <Alert
          color="#eca70a"
          style={{ width: '100%' }}
          styles={{ root: { padding: isDrawerView ? 8 : 16 } }}
          variant="light"
        >
          <AppFlexbox style={{ gap: isDrawerView ? 8 : 16, width: '100%' }}>
            <AppStack>
              <AlertCircle color="#eca70a" size={isDrawerView ? 24 : 32} />
            </AppStack>
            <AppStack style={{ gap: 5, width: '100%' }}>
              <AppText
                style={{ fontSize: isDrawerView ? 16 : 20, fontWeight: 700 }}
              >
                Important{' '}
                {cartMessageData.length === 1 ? 'message' : 'messages'}
              </AppText>

              <AppStack
                style={{ gap: 5, flex: 1, width: '100%', paddingRight: 5 }}
              >
                <List style={{ width: '100%' }}>
                  {cartMessageData.map((messageData) => {
                    const anchorProps = messageData.url
                      ? {
                          component: Link,
                          onClick: onNavigate,
                          style: {
                            fontSize: 14,
                            color: 'dodgerblue'
                          },
                          to: `${baseStoreUrl}/products/${messageData.urlHandle}`,
                          underline: 'hover'
                        }
                      : {
                          component: 'span',
                          style: {
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#000',
                            cursor: 'default'
                          },
                          underline: 'none'
                        };

                    return (
                      <List.Item key={messageData.key}>
                        {messageData.isUnavailable ? (
                          <AppText
                            style={{
                              fontSize: 14,
                              width: '100%',
                              whiteSpace: 'break-spaces'
                            }}
                          >
                            Please note that{' '}
                            <Anchor {...anchorProps}>
                              {messageData.productName}
                            </Anchor>{' '}
                            is <b>unavailable</b> for purchase. Update your cart
                            to remove this product.
                          </AppText>
                        ) : messageData.newPrice !== messageData.oldPrice ? (
                          <AppText style={{ fontSize: 14 }}>
                            The price of{' '}
                            <Anchor {...anchorProps}>
                              {messageData.productName}
                            </Anchor>{' '}
                            has{' '}
                            <b>
                              {messageData.newPrice > messageData.oldPrice
                                ? 'increased'
                                : 'decreased'}
                            </b>{' '}
                            from{' '}
                            <b>{currencyFormat(messageData.oldPrice / 100)}</b>{' '}
                            to{' '}
                            <b>{currencyFormat(messageData.newPrice / 100)}</b>.
                            Update your cart to apply the new price.
                          </AppText>
                        ) : messageData.requiresProductUpdate ? (
                          <AppText style={{ fontSize: 14 }}>
                            <Anchor {...anchorProps}>
                              {messageData.productName}
                            </Anchor>{' '}
                            requires an update. Please review your cart and
                            customize the{' '}
                            {messageData.isBundleProduct ? 'bundle' : 'product'}{' '}
                            to continue.
                          </AppText>
                        ) : messageData.requiresPrintImagesUpdate ? (
                          <AppText style={{ fontSize: 14 }}>
                            <Anchor {...anchorProps}>
                              {messageData.productName}
                            </Anchor>{' '}
                            has custom print images that require updating.
                            Please update your cart to refresh the print images
                            and continue.
                          </AppText>
                        ) : (
                          <AppText style={{ fontSize: 14 }}>
                            Please note that the details for{' '}
                            <Anchor {...anchorProps}>
                              {messageData.productName}
                            </Anchor>{' '}
                            changed. Update your cart to review the changes.
                          </AppText>
                        )}
                      </List.Item>
                    );
                  })}
                </List>
              </AppStack>

              {packageMessageData.some(
                (m) =>
                  m.isUnavailable ||
                  m.requiresPrintImagesUpdate ||
                  (m.hasChanges && !m.requiresProductUpdate)
              ) && (
                <AppFlexbox
                  style={{ marginTop: 5, alignItems: 'center', gap: 8 }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Please review the details before proceeding.{' '}
                    <Anchor
                      onClick={() => {
                        if (!submitting) {
                          onRemoveUnavailableProducts();
                        }
                      }}
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: submitting ? '#999' : 'dodgerblue'
                      }}
                      underline="always"
                    >
                      Update your cart by clicking here.
                    </Anchor>{' '}
                  </AppText>
                  {submitting && <Loader size={14} />}
                </AppFlexbox>
              )}
            </AppStack>
          </AppFlexbox>
        </Alert>
      </AppCard>
    )
  );
};

WsUnavailableProductsAlert.propTypes = {
  baseStoreUrl: PropTypes.string,
  ecomStoreCart: PropTypes.object,
  hasChangedProducts: PropTypes.bool,
  isDrawerView: PropTypes.bool,
  lineItemsInfo: PropTypes.array,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  onRemoveUnavailableProducts: PropTypes.func,
  submitting: PropTypes.bool
};

export default WsUnavailableProductsAlert;
